import React, { useRef } from "react";
import { Checkbox, Form, Input } from "antd";
import BasicButton from "../../BasicButton";
import { ReactComponent as LogoIcon } from "../../assets/appinstalls-mark.svg";

import styles from "./styles.module.scss";
import { useNavigate } from "react-router-dom";
import { adminLogin } from "../../api/axios";
import { useUser } from "../../context/UserContext";
import DotGrid from "../../motion/DotGrid/DotGrid";
import SplitText from "../../motion/SplitText";
import { gsap, prefersReducedMotion, SETTLE, useGSAP } from "../../motion/gsap";

// Drawn like the client's login screen (DS AuthScreens, Figma 895:73289):
// a 520px card on the brand-blue canvas, 56px fields, a 48px button. The
// canvas carries a quiet dot grid (React Bits DotGrid) that gives way to
// the pointer, and the card rises onto it when the page opens.
const LoginPage = () => {
  const navigate = useNavigate();
  const { refetch: refetchUser } = useUser();
  const screenRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (prefersReducedMotion()) return;
      gsap
        .timeline()
        .from(`.${styles.card}`, {
          y: 24,
          autoAlpha: 0,
          duration: 0.6,
          clearProps: SETTLE,
        })
        .from(
          `.${styles.form} .ant-form-item`,
          {
            y: 10,
            autoAlpha: 0,
            duration: 0.4,
            stagger: 0.05,
            clearProps: SETTLE,
          },
          0.25,
        )
        .from(
          `.${styles.logo}`,
          { autoAlpha: 0, duration: 0.6, clearProps: SETTLE },
          0.1,
        );
    },
    { scope: screenRef },
  );

  return (
    <section className={styles.page}>
      <div ref={screenRef} className={styles.screen}>
        {/* Dots a shade lighter than the brand blue; white under the pointer. */}
        <DotGrid
          className={styles.dots}
          dotSize={4}
          gap={22}
          baseColor="#5b6ab5"
          activeColor="#ffffff"
          proximity={130}
          glow={0.3}
          fade={0.5}
          shockStrength={3}
        />
        <LogoIcon className={styles.logo} />
        <div className={styles.card}>
          <div className={styles.heading}>
            <SplitText
              tag="h1"
              text="Sign in"
              className={styles.title}
              splitType="chars"
              stagger={30}
              delay={0.2}
            />
            <p className={styles.subtitle}>
              Welcome back to the promobile admin panel
            </p>
          </div>
          <Form
            name="normal_login"
            className={styles.form}
            onFinish={async (values) => {
              await adminLogin({
                email: values.email,
                password: values.password,
                secret: values.secret,
              })
                // The sidebar and the super admin pages read the role.
                .then(refetchUser)
                .then(() => navigate("/users"));
            }}
            layout="vertical"
            requiredMark="optional"
          >
            <Form.Item
              name="email"
              rules={[
                {
                  type: "email",
                  required: true,
                  message: "Please input your Email!",
                },
              ]}
            >
              <Input className={styles.input} placeholder="Email" />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: "Please input your Password!",
                },
              ]}
            >
              <Input.Password
                className={styles.input}
                type="password"
                placeholder="Password"
              />
            </Form.Item>
            <Form.Item
              name="secret"
              rules={[
                {
                  type: "string",
                  required: true,
                  message: "Please input the admin secret!",
                },
              ]}
            >
              <Input className={styles.input} placeholder="Admin secret" />
            </Form.Item>
            <Form.Item>
              <Form.Item valuePropName="checked" noStyle>
                <Checkbox className={styles.checkbox}>Remember me</Checkbox>
              </Form.Item>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <BasicButton
                type="submit"
                title="Log in"
                variant="primary"
                sx={{
                  width: "100%",
                  height: "48px",
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
    </section>
  );
};

export default LoginPage;
